import { useState, useEffect } from "react";
import { useClinicDetail, useUpdateClinic } from "@api/hooks";
import { showSuccess, showError, Loading, Button, Card } from "@common";
import { REPORT_COLUMN_CONFIGS } from "@utils";

export default function ClinicSettings({ clinicId }) {
  const { data: clinicDetail, isLoading } = useClinicDetail(clinicId);
  const { mutate: updateClinic, isPending } = useUpdateClinic();

  const clinic = clinicDetail?.clinic;
  const [localHidden, setLocalHidden] = useState([]);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (clinic) {
      setLocalHidden(clinic.hidden_columns ?? []);
      setIsDirty(false);
    }
  }, [clinic]);

  function toggleColumn(key) {
    setLocalHidden((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      const original = (clinic?.hidden_columns ?? []).slice().sort().join(",");
      setIsDirty(next.slice().sort().join(",") !== original);
      return next;
    });
  }

  function handleSave() {
    updateClinic(
      { id: clinic.id, data: { hidden_columns: localHidden } },
      {
        onSuccess: () => {
          showSuccess("Column settings saved.");
          setIsDirty(false);
        },
        onError: (err) => {
          const msg = err?.response?.data?.hidden_columns?.[0] || "Failed to save settings.";
          showError(msg);
        },
      },
    );
  }

  if (isLoading) {
    return <Loading size="md" text="Loading settings..." />;
  }

  if (!clinic) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-sm text-palette-gray-secondary">Clinic not found.</div>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-6 max-w-lg">
      <Card title="Column Visibility">
        <p className="text-sm text-palette-gray-secondary mb-5">
          Toggle off columns that should not appear in this clinic&apos;s reports.
        </p>

        <div className="divide-y divide-palette-gray-subtle">
          {REPORT_COLUMN_CONFIGS.map(({ key, label }) => {
            const isVisible = !localHidden.includes(key);
            return (
              <div key={key} className="flex items-center justify-between py-3">
                <span className="text-sm text-palette-gray-text">{label}</span>
                <button
                  type="button"
                  onClick={() => toggleColumn(key)}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-palette-indigo-label focus:ring-offset-2 ${
                    isVisible ? "bg-palette-indigo-badge" : "bg-palette-gray-divider"
                  }`}
                  role="switch"
                  aria-checked={isVisible}
                  aria-label={label}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      isVisible ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <Button
            size="sm"
            variant={isDirty && !isPending ? "success" : "gray"}
            onClick={handleSave}
            disabled={!isDirty || isPending}
          >
            {isPending ? "Saving..." : "Save"}
          </Button>
        </div>

        {isDirty && (
          <div className="mt-2 flex items-center justify-end gap-3">
            <span className="text-xs text-palette-amber-badge font-medium">Unsaved changes</span>
          </div>
        )}
      </Card>
    </div>
  );
}
